/**
 * Every write to the ledger goes through here.
 *
 * Two guarantees this file exists to keep:
 *   1. A local change is written to IndexedDB and queued for sync in the same
 *      IndexedDB transaction, so a change can never be saved-but-unqueued or
 *      queued-but-unsaved.
 *   2. Nothing is ever hard-deleted. A deletion is a `deletedAt` stamp, which
 *      is what lets a removal replicate instead of silently reappearing after
 *      the next pull.
 */
import {
  createTransaction,
  merchantKey,
  suggestCategory,
  withRefreshedDedupeKey,
  type Category,
  type MerchantRule,
  type Transaction,
  type TransactionType,
} from '@wis/shared';
import { db, type WhatISpentDatabase } from './database.js';

export interface SaveOptions {
  database?: WhatISpentDatabase;
  /** Skip the sync queue -- used when applying records that came *from* the server. */
  fromServer?: boolean;
}

/** Write a transaction and queue it, atomically. */
export async function saveTransaction(
  transaction: Transaction,
  options: SaveOptions = {},
): Promise<Transaction> {
  const database = options.database ?? db;
  await database.transaction('rw', database.transactions, database.syncQueue, async () => {
    await database.transactions.put(transaction);
    if (!options.fromServer) {
      await database.syncQueue.add({
        transactionId: transaction.id,
        operation: 'upsert',
        queuedAt: new Date().toISOString(),
        attempts: 0,
      });
    }
  });
  return transaction;
}

export async function saveTransactions(
  transactions: readonly Transaction[],
  options: SaveOptions = {},
): Promise<number> {
  const database = options.database ?? db;
  if (transactions.length === 0) return 0;

  await database.transaction('rw', database.transactions, database.syncQueue, async () => {
    await database.transactions.bulkPut(transactions as Transaction[]);
    if (!options.fromServer) {
      const queuedAt = new Date().toISOString();
      await database.syncQueue.bulkAdd(
        transactions.map((transaction) => ({
          transactionId: transaction.id,
          operation: 'upsert' as const,
          queuedAt,
          attempts: 0,
        })),
      );
    }
  });
  return transactions.length;
}

export interface AddTransactionInput {
  amountSen: number;
  type?: TransactionType;
  merchant?: string;
  categoryId?: string;
  accountLabel?: string;
  occurredAt?: string;
  note?: string | null;
  source?: Transaction['source'];
  importConfidence?: number;
  reviewStatus?: Transaction['reviewStatus'];
  currency?: string;
}

/** The quick-entry path: build a complete record, then save and queue it. */
export async function addTransaction(
  input: AddTransactionInput,
  options: SaveOptions = {},
): Promise<Transaction> {
  const transaction = createTransaction({
    ...input,
    note: input.note ?? null,
  });
  return saveTransaction(transaction, options);
}

export interface UpdateTransactionInput {
  amountSen?: number;
  type?: TransactionType;
  merchant?: string;
  categoryId?: string;
  accountLabel?: string;
  occurredAt?: string;
  note?: string | null;
  reviewStatus?: Transaction['reviewStatus'];
}

/**
 * Edit an existing record.
 * The dedupe key is re-derived, because an edited amount or time means the
 * record now fingerprints as a different payment.
 */
export async function updateTransaction(
  id: string,
  patch: UpdateTransactionInput,
  options: SaveOptions = {},
): Promise<Transaction | null> {
  const database = options.database ?? db;
  const existing = await database.transactions.get(id);
  if (!existing) return null;

  const merchantChanged = patch.merchant !== undefined && patch.merchant !== existing.merchantOriginal;

  const next = withRefreshedDedupeKey({
    ...existing,
    ...(patch.amountSen !== undefined ? { amountSen: Math.abs(patch.amountSen) } : {}),
    ...(patch.type !== undefined ? { type: patch.type } : {}),
    ...(merchantChanged
      ? {
          merchantOriginal: patch.merchant ?? '',
          merchantNormalized: createTransaction({
            amountSen: 1,
            merchant: patch.merchant ?? '',
          }).merchantNormalized,
        }
      : {}),
    ...(patch.categoryId !== undefined ? { categoryId: patch.categoryId } : {}),
    ...(patch.accountLabel !== undefined ? { accountLabel: patch.accountLabel } : {}),
    ...(patch.occurredAt !== undefined ? { occurredAt: patch.occurredAt } : {}),
    ...(patch.note !== undefined ? { note: patch.note } : {}),
    ...(patch.reviewStatus !== undefined ? { reviewStatus: patch.reviewStatus } : {}),
    updatedAt: new Date().toISOString(),
  });

  return saveTransaction(next, options);
}

/** Confirm a record out of the review inbox, applying any corrections at once. */
export async function confirmTransaction(
  id: string,
  patch: UpdateTransactionInput = {},
  options: SaveOptions = {},
): Promise<Transaction | null> {
  return updateTransaction(
    id,
    { ...patch, reviewStatus: 'confirmed' },
    options,
  ).then(async (transaction) => {
    if (!transaction) return null;
    const database = options.database ?? db;
    // A confirmed record is a user decision, so its confidence is now total.
    const next = { ...transaction, importConfidence: 1 };
    await database.transactions.put(next);
    return next;
  });
}

/** Soft delete. The record stays so the deletion can replicate. */
export async function deleteTransaction(
  id: string,
  options: SaveOptions = {},
): Promise<Transaction | null> {
  const database = options.database ?? db;
  const existing = await database.transactions.get(id);
  if (!existing) return null;

  const now = new Date().toISOString();
  return saveTransaction({ ...existing, deletedAt: now, updatedAt: now }, options);
}

/** Undo a soft delete, for the "Undo" action on the delete toast. */
export async function restoreTransaction(
  id: string,
  options: SaveOptions = {},
): Promise<Transaction | null> {
  const database = options.database ?? db;
  const existing = await database.transactions.get(id);
  if (!existing) return null;
  const now = new Date().toISOString();
  return saveTransaction({ ...existing, deletedAt: null, updatedAt: now }, options);
}

export async function listActiveTransactions(
  database: WhatISpentDatabase = db,
): Promise<Transaction[]> {
  const all = await database.transactions.toArray();
  return all
    .filter((transaction) => !transaction.deletedAt)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

export async function listReviewQueue(database: WhatISpentDatabase = db): Promise<Transaction[]> {
  const all = await database.transactions.where('reviewStatus').equals('needs-review').toArray();
  return all
    .filter((transaction) => !transaction.deletedAt)
    .sort((a, b) => a.importConfidence - b.importConfidence || b.occurredAt.localeCompare(a.occurredAt));
}

export interface MerchantHistoryEntry {
  merchant: string;
  categoryId: string;
  count: number;
  lastUsedAt: string;
}

/**
 * What the user has actually done before, used for merchant suggestions and
 * for the history tier of the category rules. Built from confirmed records
 * only -- an unreviewed guess must not teach the app a habit.
 */
export async function buildMerchantHistory(
  database: WhatISpentDatabase = db,
): Promise<MerchantHistoryEntry[]> {
  const all = await database.transactions.toArray();
  const groups = new Map<string, MerchantHistoryEntry>();

  for (const transaction of all) {
    if (transaction.deletedAt) continue;
    if (transaction.reviewStatus !== 'confirmed') continue;
    const name = transaction.merchantNormalized || transaction.merchantOriginal;
    if (!name) continue;

    const key = `${merchantKey(name)}|${transaction.categoryId}`;
    const entry = groups.get(key);
    if (entry) {
      entry.count += 1;
      if (transaction.occurredAt > entry.lastUsedAt) entry.lastUsedAt = transaction.occurredAt;
    } else {
      groups.set(key, {
        merchant: name,
        categoryId: transaction.categoryId,
        count: 1,
        lastUsedAt: transaction.occurredAt,
      });
    }
  }

  return [...groups.values()].sort((a, b) => b.count - a.count);
}

/** Category suggestion with everything this device knows plugged in. */
export async function suggestCategoryForMerchant(
  merchant: string,
  type: TransactionType = 'expense',
  database: WhatISpentDatabase = db,
): Promise<ReturnType<typeof suggestCategory>> {
  const [rules, history] = await Promise.all([
    database.merchantRules.toArray(),
    buildMerchantHistory(database),
  ]);
  return suggestCategory({ merchant, type, rules, history });
}

export async function listCategories(database: WhatISpentDatabase = db): Promise<Category[]> {
  const categories = await database.categories.toArray();
  return categories.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export async function saveCategory(
  category: Category,
  database: WhatISpentDatabase = db,
): Promise<void> {
  await database.categories.put({ ...category, updatedAt: new Date().toISOString() });
}

/**
 * Archive rather than delete, and move anything filed under it somewhere safe.
 * Deleting a category must never quietly delete the spending inside it.
 */
export async function archiveCategory(
  id: string,
  moveTo: string,
  database: WhatISpentDatabase = db,
): Promise<number> {
  let moved = 0;
  await database.transaction('rw', database.categories, database.transactions, database.syncQueue, async () => {
    const affected = await database.transactions.where('categoryId').equals(id).toArray();
    const now = new Date().toISOString();
    const updated = affected.map((transaction) => ({
      ...transaction,
      categoryId: moveTo,
      updatedAt: now,
    }));
    await database.transactions.bulkPut(updated);
    await database.syncQueue.bulkAdd(
      updated.map((transaction) => ({
        transactionId: transaction.id,
        operation: 'upsert' as const,
        queuedAt: now,
        attempts: 0,
      })),
    );
    const category = await database.categories.get(id);
    if (category) await database.categories.put({ ...category, archived: true, updatedAt: now });
    moved = updated.length;
  });
  return moved;
}

export async function listRules(database: WhatISpentDatabase = db): Promise<MerchantRule[]> {
  const rules = await database.merchantRules.toArray();
  return rules.sort((a, b) => b.priority - a.priority || a.pattern.localeCompare(b.pattern));
}

export async function saveRule(
  rule: MerchantRule,
  database: WhatISpentDatabase = db,
): Promise<void> {
  await database.merchantRules.put({ ...rule, updatedAt: new Date().toISOString() });
}

export async function deleteRule(id: string, database: WhatISpentDatabase = db): Promise<void> {
  await database.merchantRules.delete(id);
}

/**
 * Re-apply the rules to records the user has not reviewed.
 * Confirmed records are left exactly as they are: a rule added today must not
 * rewrite a decision made last month.
 */
export async function applyRulesToUnreviewed(database: WhatISpentDatabase = db): Promise<number> {
  const [rules, history, all] = await Promise.all([
    database.merchantRules.toArray(),
    buildMerchantHistory(database),
    database.transactions.toArray(),
  ]);

  const now = new Date().toISOString();
  const changed: Transaction[] = [];

  for (const transaction of all) {
    if (transaction.deletedAt) continue;
    if (transaction.reviewStatus === 'confirmed') continue;
    const suggestion = suggestCategory({
      merchant: transaction.merchantNormalized,
      type: transaction.type,
      rules,
      history,
    });
    if (suggestion.categoryId !== transaction.categoryId && suggestion.reason === 'user-rule') {
      changed.push({ ...transaction, categoryId: suggestion.categoryId, updatedAt: now });
    }
  }

  await saveTransactions(changed, { database });
  return changed.length;
}

/** Erase everything on this device. Callers must confirm before calling. */
export async function deleteAllLocalData(database: WhatISpentDatabase = db): Promise<void> {
  // Every table at once: the array form, because the tuple overloads stop at five.
  await database.transaction(
    'rw',
    [
      database.transactions,
      database.categories,
      database.accounts,
      database.merchantRules,
      database.syncQueue,
      database.meta,
    ],
    async () => {
      await Promise.all([
        database.transactions.clear(),
        database.categories.clear(),
        database.accounts.clear(),
        database.merchantRules.clear(),
        database.syncQueue.clear(),
        database.meta.clear(),
      ]);
    },
  );
}
